import React from 'react';
import { X, UserCircle2, PieChart } from 'lucide-react';

const formatAmount = (value) =>
  value != null && !Number.isNaN(Number(value)) ? Number(value).toFixed(2) : 'N/A';

const LeaveBalanceDetailModal = ({ member, balances = [], onClose }) => {

  const statusConfig = member.status === 'On Leave'
    ? { bg: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-200' }
    : { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${member.name} leave balances`}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <UserCircle2 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{member.name}</h3>
              <p className="text-sm text-gray-500">{member.role}</p>
            </div>
            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${statusConfig.bg} ${statusConfig.text} ${statusConfig.ring}`}>
              {member.status}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <h4 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <PieChart className="w-6 h-6 text-indigo-600" />
            Leave Balance
          </h4>

          {balances.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No leave balance available.</p>
          ) : (
            <div className="space-y-3">
              {balances.map((b) => {
                const amount = Number(b.BalanceRemaining) || 0;
                const pct = Math.min(100, Math.max(0, (amount / 25) * 100));
                const isEmpty = amount === 0;
                return (
                  <div
                    key={b.BalanceType}
                    className="border border-gray-100 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{b.BalanceType}</p>
                        {b.AccrualStartDate && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {b.AccrualStartDate}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className={`text-2xl font-bold ${isEmpty ? 'text-gray-400' : 'text-indigo-600'}`}>
                          {formatAmount(b.BalanceRemaining)}
                        </p>
                        <p className="text-xs text-gray-500">{b.unit} remaining</p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                      <div
                        className={`h-1.5 rounded-full transition-all ${isEmpty ? 'bg-gray-300' : 'bg-indigo-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50 rounded-b-xl">
          <p className="text-xs text-gray-400">Leave balances are sourced from EssLeaveBalances.</p>
        </div>
      </div>
    </div>
  );
};

export default LeaveBalanceDetailModal;
